import { inject, Injectable } from '@angular/core';
import { ApiDataAccess } from '@softline/core';
import {
  SOFTLINE_API_MDC_STOCK_POSTING,
  SOFTLINE_API_MDC_STOCK_POSTING_DETAIL,
  SOFTLINE_API_MDC_STOCK_POSTING_FINALIZE,
  SOFTLINE_API_MDC_STOCK_POSTING_SIGNATURE,
} from '../mdc-stock-posting.api';
import {
  StockPosting,
  StockPostingCreate,
  StockPostingFinalize,
  StockPostingPatch,
} from '../types/stock-posting';
import { filter, firstValueFrom, toArray } from 'rxjs';

@Injectable()
export class StockPostingRepository {
  private readonly dataAccess = inject(ApiDataAccess);

  /** The user's currently open stock posting document (empty when none exists). */
  loadOpen(): Promise<StockPosting | null> {
    return this.dataAccess.get<StockPosting | null>({
      path: SOFTLINE_API_MDC_STOCK_POSTING,
    });
  }

  create(body: StockPostingCreate): Promise<StockPosting> {
    return this.dataAccess.create<StockPostingCreate, StockPosting>(
      { path: SOFTLINE_API_MDC_STOCK_POSTING },
      body
    );
  }

  patch(id: number, body: Partial<StockPostingPatch>): Promise<StockPosting> {
    return this.dataAccess.patch<Partial<StockPostingPatch>, StockPosting>(
      { path: SOFTLINE_API_MDC_STOCK_POSTING_DETAIL, pathParams: { id } },
      body
    );
  }

  delete(id: number): Promise<StockPosting> {
    return this.dataAccess.delete<StockPosting>({
      path: SOFTLINE_API_MDC_STOCK_POSTING_DETAIL,
      pathParams: { id },
    });
  }

  finalize(id: number, body: StockPostingFinalize): Promise<StockPosting> {
    return this.dataAccess.create<StockPostingFinalize, StockPosting>(
      { path: SOFTLINE_API_MDC_STOCK_POSTING_FINALIZE, pathParams: { id } },
      body
    );
  }

  signDocument(
    id: string | number,
    signature: Blob,
    signerName?: string | null
  ): Promise<void> {
    const file = new File([signature], 'signature.png', {
      type: signature.type || 'image/png',
    });
    const body: { file: File; signerName?: string } = { file };
    const name = signerName?.trim();
    if (name) body.signerName = name;
    return firstValueFrom(
      this.dataAccess
        .upload<typeof body, void>(
          { path: SOFTLINE_API_MDC_STOCK_POSTING_SIGNATURE, pathParams: { id } },
          body
        )
        .pipe(filter((event) => event?.type === 'response'))
    ).then(() => undefined);
  }

  async loadSignature(id: string | number): Promise<Blob | null> {
    const events = await firstValueFrom(
      this.dataAccess
        .download({ path: SOFTLINE_API_MDC_STOCK_POSTING_SIGNATURE, pathParams: { id } })
        .pipe(toArray())
    );
    const response = events.find((event) => event?.type === 'response');
    const blob = (response as { response?: Blob | ArrayBuffer } | undefined)?.response;
    return blob instanceof Blob ? blob : null;
  }
}
